import styled from "styled-components";
import { Leaf, MapPin, Award, Users } from "lucide-react";
import { usePage } from "@inertiajs/react";
import {
  Container,
  SectionHeader,
  Subtitle,
  Title,
  Description,
} from "./Section";
import { useLandingLanguage } from "../../../contexts/landing-language";

const AboutSection = styled.section`
  padding: 120px 24px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 80px 24px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$withImage ? "1fr 1fr" : "1fr")};
  gap: 80px;
  align-items: center;
  margin-bottom: 100px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
`;

const TextContent = styled.div`
  h3 {
    font-size: 36px;
    font-weight: 300;
    color: #1a1a1a;
    margin-bottom: 28px;
    line-height: 1.3;

    strong {
      font-weight: 600;
    }
  }

  p {
    font-size: 16px;
    color: #666;
    line-height: 1.9;
    margin-bottom: 20px;
    font-weight: 300;
  }
`;

const ImageContainer = styled.div`
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -20px;
    left: -20px;
    right: 20px;
    bottom: 20px;
    border: 1px solid #c9a86c;
    z-index: 0;
  }
`;

// The photograph comes from the CMS, never from a bundled asset. This used to
// hardcode a local file that carried a third-party photographer's copyright
// watermark and was not licensed to this business — it was shipping on the live
// property page. When the CMS has no image the whole column is dropped rather
// than framing an empty box. Details in wiki/seo/README.md.
const StyledImage = styled.div`
  aspect-ratio: 4/3;
  /* A background-image cannot report a load failure the way <img onError> can,
     so a broken CMS URL would leave a bare white box inside the gold frame.
     The tint underneath makes that degrade to a deliberate-looking panel. */
  background-color: #f0ebe3;
  background-image: url(${(props) => props.$src});
  background-position: center;
  background-size: cover;
  position: relative;
  z-index: 1;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  padding: 40px 28px;
  background: #faf8f5;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.08);
    border-color: #e5e1dc;
    background: #ffffff;
  }
`;

const IconContainer = styled.div`
  width: 70px;
  height: 70px;
  background: transparent;
  border: 1px solid #c9a86c;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #c9a86c;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  ${FeatureCard}:hover & {
    background: #1a1a1a;
    border-color: #1a1a1a;
    color: #faf8f5;
  }
`;

const FeatureTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
`;

const FeatureDesc = styled.p`
  font-size: 14px;
  color: #8b7355;
  line-height: 1.7;
  font-weight: 400;
`;

const About = () => {
  const { t } = useLandingLanguage();
  const { landing_content: landingContent } = usePage().props;
  const a = t.about;

  // Prefer the second hero upload so the About block does not simply repeat the
  // image already at the top of the page.
  const banners = landingContent?.hero_banner_img_urls || [];
  const aboutImage = banners[1] || banners[0] || null;

  return (
    <AboutSection id="about">
      <Container>
        <SectionHeader>
          <Subtitle>{a.subtitle}</Subtitle>
          <Title>
            {a.title} <strong>Hebe Hotel</strong>
          </Title>
          <Description>{a.description}</Description>
        </SectionHeader>

        <ContentGrid $withImage={Boolean(aboutImage)}>
          {aboutImage ? (
            <ImageContainer>
              <StyledImage $src={aboutImage} />
            </ImageContainer>
          ) : null}
          <TextContent>
            <h3>{a.contentHeading}</h3>
            <p>{a.p1}</p>
            <p>{a.p2}</p>
          </TextContent>
        </ContentGrid>

        <FeaturesGrid>
          <FeatureCard>
            <IconContainer>
              <Leaf size={32} />
            </IconContainer>
            <FeatureTitle>{a.features.eco.title}</FeatureTitle>
            <FeatureDesc>{a.features.eco.desc}</FeatureDesc>
          </FeatureCard>

          <FeatureCard>
            <IconContainer>
              <MapPin size={32} />
            </IconContainer>
            <FeatureTitle>{a.features.location.title}</FeatureTitle>
            <FeatureDesc>{a.features.location.desc}</FeatureDesc>
          </FeatureCard>

          <FeatureCard>
            <IconContainer>
              <Award size={32} />
            </IconContainer>
            <FeatureTitle>{a.features.service.title}</FeatureTitle>
            <FeatureDesc>{a.features.service.desc}</FeatureDesc>
          </FeatureCard>

          <FeatureCard>
            <IconContainer>
              <Users size={32} />
            </IconContainer>
            <FeatureTitle>{a.features.community.title}</FeatureTitle>
            <FeatureDesc>{a.features.community.desc}</FeatureDesc>
          </FeatureCard>
        </FeaturesGrid>
      </Container>
    </AboutSection>
  );
};

export default About;
